import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  LinearProgress,
  Button,
  Divider,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useReportDataService } from "../../services/ReportDataService";

// 🔍 Only required columns with updated labels
const columnDefs = [
  { key: "UserId", label: "Employee Id", flex: 1.2 },
  { key: "Email", label: "Email Id", flex: 2 },
  { key: "AuthenticationMessage", label: "Auth Response", flex: 3 },
  { key: "UpdatedOnFormatted", label: "Attempted On", flex: 1.5 },
];

const ROW_HEIGHT = 32;

const FailedLoginAttempts = ({ empID = "All" }) => {
  const { fetchFailedLoginAttempts } = useReportDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const [logsDate, setLogsDate] = useState(dayjs());
  const listRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const formattedDate = dayjs(logsDate).format("YYYY-MM-DD");
        const params = {
          employeeID: empID,
          logsDate: formattedDate,
        };
        const response = await fetchFailedLoginAttempts(params);
        console.log("Fetched Data:", response);
        setData(response || []);
      } catch (err) {
        console.error("Error fetching failed login data", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [logsDate, empID]);

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      columnDefs.every(({ key }) => {
        const term = (search[key] || "").toLowerCase();
        const value = item[key]?.toString().toLowerCase() || "";
        return value.includes(term);
      })
    );
  }, [data, search]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    if (!row) return null;

    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.775rem",
          lineHeight: 1.4,
          minHeight: ROW_HEIGHT,
        }}
      >
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1,
            }}
          >
            {row[key] || "-"}
          </Box>
        ))}
      </Box>
    );
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FailedLoginAttempts");
    XLSX.writeFile(wb, "FailedLoginAttempts.xlsx");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Typography variant="h2">Failed Login Attempts</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <DesktopDatePicker
              label="Log Date"
              inputFormat="DD/MM/YYYY"
              value={logsDate}
              onChange={(newValue) => newValue && setLogsDate(newValue)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <Button variant="contained" onClick={handleExport}>
              Export to Excel
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {loading ? (
          <LinearProgress />
        ) : (
          <Box component={Paper}>
            {/* Header Filter Row */}
            <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
              {columnDefs.map(({ key, label, flex }) => (
                <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                  <TextField
                    label={label}
                    variant="standard"
                    value={search[key] || ""}
                    onChange={(e) =>
                      setSearch((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Box>
              ))}
              <Box sx={{ width: 32 }} />
            </Box>

            <Box sx={{ height: "65vh" }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemCount={filteredData.length}
                    itemSize={() => ROW_HEIGHT}
                    itemKey={(index) => index}
                    ref={listRef}
                  >
                    {Row}
                  </List>
                )}
              </AutoSizer>
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default FailedLoginAttempts;
